/** Where the studio starts: what exists, and the next useful thing to do. */
import { api } from "../../lib/api.ts";
import { Link } from "../../lib/router.tsx";
import { useAsync } from "../../lib/useAsync.ts";
import type { DomainRow, EntityRow, FieldRow } from "../../lib/types.ts";
import { GuideInvite, PageHead, Tiles } from "../../components/Page.tsx";

export function ConfigOverview() {
  const entities = useAsync(() => api.get<{ entities: EntityRow[] }>("/api/entities"), []);
  const domains = useAsync(() => api.get<{ domains: DomainRow[] }>("/api/domains"), []);
  const fields = useAsync(() => api.get<{ fields: FieldRow[] }>("/api/fields"), []);

  const entityCount = entities.data?.entities.length ?? 0;
  const fieldCount = fields.data?.fields.length ?? 0;
  const domainCount = domains.data?.domains.length ?? 0;

  return (
    <div className="stack">
      <PageHead
        title="Product configuration"
        sub="Fields are defined once and reused. A product is a selection of them — which is why a new product is a morning's work rather than a release."
      />

      <GuideInvite />

      <Tiles
        tiles={[
          { label: "Fields in the library", value: fieldCount, tone: "accent" },
          { label: "Entities", value: entityCount, tone: "info" },
          { label: "Value domains", value: domainCount, tone: "ok" },
          { label: "Products", value: "—", tone: "muted" },
        ]}
      />

      <div className="card">
        <div className="card-head"><h2>How this fits together</h2></div>
        <div className="card-body stack">
          <div className="steps">
            <Step
              number={1} title="Entities" to="/entities" done={entityCount > 0}
              body="What a field describes — an applicant, a location, a vehicle. An entity that repeats becomes a grid on the form."
            />
            <Step
              number={2} title="Value domains" to="/domains" done={domainCount > 0}
              body="The coded lists choice fields offer. Shared, so two products offer the same answers to the same question."
            />
            <Step
              number={3} title="Fields" to="/fields" done={fieldCount > 0}
              body="Every question the platform can ask. Defined once, attached to any number of products."
            />
            <Step
              number={4} title="Products" to="/products" done={false}
              body="A versioned selection of fields, arranged into sections. Publishing freezes it and the portal starts asking it."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, body, to, done }: { number: number; title: string; body: string; to: string; done: boolean }) {
  return (
    <Link to={to} className={`step${done ? " is-done" : ""}`}>
      <span className="step-number">{done ? "✓" : number}</span>
      <span className="step-text">
        <span className="step-title">{title}</span>
        <span className="step-body">{body}</span>
      </span>
    </Link>
  );
}
